import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import "@/styles/genie-banner.css";
import { ArrowUpDown, Grid3X3, Hourglass, Lock, Loader2, Plus, TableProperties } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { ResumeSetupPrompt } from "@/components/agent/recovery/ResumeSetupPrompt";
import BrandSpinner from "@/components/ui/BrandSpinner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchInput } from "@/components/ui/search-input";
import {
  useAgentActivityCounts,
  useAgentGroupCounts,
  useAgentGroups,
  useAgents,
  useOptimisticAgentReorder,
} from "@/hooks/agent";
import { useAgentCreationNotifications } from "@/hooks/agent/useAgentCreationNotifications";
import { useSetupRetryNotifications } from "@/hooks/agent/useSetupRetryNotifications";
import { useUnfinishedSetups } from "@/hooks/agent/useUnfinishedSetups";
import { useGenieAccess } from "@/hooks/billing/useAccessLimits";
import { AgentGroup } from "@/types/agent/agent-group";
import LearnMoreButton from "@/components/learn/LearnMoreButton";
import MagicGenieCard from "./components/MagicGenieCard";
import GenieTableView from "./components/GenieTableView";
import { GroupFilterTabs } from "./components/GroupFilterTabs";
import { GroupManagementDialog } from "./components/GroupManagementDialog";
import UnlockGenieCard from "./components/UnlockGenieCard";
import UpgradePromptCard from "./components/UpgradePromptCard";

type GenieSortMode = "custom" | "name-asc" | "name-desc" | "newest" | "oldest";

const SORT_OPTIONS: { value: GenieSortMode; label: string }[] = [
  { value: "custom", label: "Custom order" },
  { value: "name-asc", label: "Name A–Z" },
  { value: "name-desc", label: "Name Z–A" },
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

const nameCollator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

const createdAtTime = (value?: string | null) => (value ? Date.parse(value) || 0 : 0);

const MyGeniesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [genieSearchTerm, setGenieSearchTerm] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const {
    data: agentsData,
    isLoading: agentsLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useAgents(250, selectedGroupId ? { groupId: selectedGroupId } : undefined);
  const [viewMode, setViewMode] = useState<"grid" | "table">(() => {
    const stored = localStorage.getItem("genie-view-mode");
    return (stored === "grid" || stored === "table") ? stored : "grid";
  });
  const [sortMode, setSortMode] = useState<GenieSortMode>(() => {
    const stored = localStorage.getItem("genie-sort-mode");
    return SORT_OPTIONS.some((option) => option.value === stored) ? (stored as GenieSortMode) : "custom";
  });
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AgentGroup | null>(null);
  const [ownershipFilter, setOwnershipFilter] = useState<"all" | "personal" | "team">("all");
  const agents = agentsData?.allAgents || [];
  const totalAgents = agentsData?.total || 0;
  const { data: groups = [] } = useAgentGroups();
  const { data: serverGroupCounts } = useAgentGroupCounts();
  const { data: activityCounts } = useAgentActivityCounts(agents.map((agent) => agent.id));
  const { reorderAgents, isReordering } = useOptimisticAgentReorder();
  const { unfinishedSetups } = useUnfinishedSetups();

  const { personalCount, teamCount } = useMemo(() => {
    const personal = agents.filter((agent) => agent.user_id === user?.id).length;
    const team = agents.filter((agent) => agent.user_id !== user?.id).length;
    return { personalCount: personal, teamCount: team };
  }, [agents, user?.id]);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useSetupRetryNotifications();
  useAgentCreationNotifications();

  const {
    canCreateGenie,
    isAtLimit,
    requiresUpgrade,
    personal,
    team: teamLimits,
    isLoading: limitsLoading,
  } = useGenieAccess();

  // Whichever scope is actually blocking creation drives the upgrade-prompt copy
  const blockingScope = !personal.canCreateGenie ? personal : teamLimits ?? personal;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleViewModeChange = (mode: "grid" | "table") => {
    setViewMode(mode);
    localStorage.setItem("genie-view-mode", mode);
  };

  const handleSortModeChange = (mode: GenieSortMode) => {
    setSortMode(mode);
    localStorage.setItem("genie-sort-mode", mode);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const currentList = filteredAgents;
    const oldIndex = currentList.findIndex((agent) => agent.id === active.id);
    const newIndex = currentList.findIndex((agent) => agent.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(currentList, oldIndex, newIndex);
      reorderAgents(newOrder);
    }
  };

  const filteredAgents = useMemo(() => {
    let result = agents;

    const seenIds = new Set<string>();
    result = result.filter((agent) => {
      if (seenIds.has(agent.id)) return false;
      seenIds.add(agent.id);
      return true;
    });

    if (ownershipFilter === "personal") {
      result = result.filter((agent) => agent.user_id === user?.id);
    } else if (ownershipFilter === "team") {
      result = result.filter((agent) => agent.user_id !== user?.id);
    }

    if (genieSearchTerm) {
      const searchLower = genieSearchTerm.toLowerCase();
      result = result.filter((agent) => {
        const nameMatch = agent.name?.toLowerCase().includes(searchLower);
        const descriptionMatch = agent.description?.toLowerCase().includes(searchLower);
        const welcomeMatch = (agent.welcome_message ?? agent.page?.welcome_message)?.toLowerCase().includes(searchLower);
        const categoryMatch = agent.category?.toLowerCase().includes(searchLower);

        return nameMatch || descriptionMatch || welcomeMatch || categoryMatch;
      });
    }

    if (sortMode === "name-asc" || sortMode === "name-desc") {
      const direction = sortMode === "name-asc" ? 1 : -1;
      result = [...result].sort((a, b) => direction * nameCollator.compare(a.name ?? "", b.name ?? ""));
    } else if (sortMode === "newest" || sortMode === "oldest") {
      const direction = sortMode === "oldest" ? 1 : -1;
      result = [...result].sort((a, b) => direction * (createdAtTime(a.created_at) - createdAtTime(b.created_at)));
    }

    return result;
  }, [agents, genieSearchTerm, selectedGroupId, ownershipFilter, user?.id, sortMode]);

  const isDragEnabled = !genieSearchTerm && sortMode === "custom";
  const activeSortLabel = SORT_OPTIONS.find((option) => option.value === sortMode)?.label;

  return (
    <div className="min-h-screen bg-[#F9FAFC]" style={{ touchAction: "pan-y" }}>
      <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1920px] relative">
        <div className="w-full">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between md:pr-[104px] gap-4 mb-6 md:mb-8">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src="/genie-mascots/role/support.png"
                alt="My Genies"
                className="h-9 w-9 md:h-11 md:w-11"
              />
              <div className="min-w-0">
                <h1 className="text-xl md:text-3xl font-bold text-[#222B45] leading-tight">
                  My Genies
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <p className="text-sm text-[#64748B] hidden sm:block">
                    Manage and interact with your Genies
                  </p>
                  <LearnMoreButton topic="my-genies" variant="link" />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {!limitsLoading && !personal.canCreateGenie && (
                <div
                  className="flex items-center gap-3 px-4 py-2 rounded-2xl text-white"
                  style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
                >
                  <Lock className="w-4 h-4 shrink-0" />
                  <p className="text-sm font-medium whitespace-nowrap">
                    Personal genie limit reached ({personal.currentGenieCount}/{personal.maxGenies})
                  </p>
                  <button
                    onClick={() => navigate("/pricing")}
                    className="shrink-0 text-sm font-semibold bg-white/20 hover:bg-white/30 transition-colors px-3 py-1 rounded-full"
                  >
                    Upgrade
                  </button>
                </div>
              )}
              {!limitsLoading && teamLimits && !teamLimits.canCreateGenie && (
                <div
                  className="flex items-center gap-3 px-4 py-2 rounded-2xl text-white"
                  style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
                >
                  <Lock className="w-4 h-4 shrink-0" />
                  <p className="text-sm font-medium whitespace-nowrap">
                    Team genie limit reached ({teamLimits.currentGenieCount}/{teamLimits.maxGenies})
                  </p>
                  <button
                    onClick={() => navigate("/pricing")}
                    className="shrink-0 text-sm font-semibold bg-white/20 hover:bg-white/30 transition-colors px-3 py-1 rounded-full"
                  >
                    Upgrade
                  </button>
                </div>
              )}
              {canCreateGenie && (
                <Button
                  onClick={() => navigate("/user-hub/genie-setup")}
                  className="h-10 gap-2 bg-gradient-to-r from-genie-primary to-genie-secondary font-semibold text-white shadow-md shadow-genie-primary/20 hover:from-genie-primary-hover hover:to-genie-secondary"
                >
                  <Plus className="h-4 w-4" />
                  Create a New Genie
                </Button>
              )}
            </div>
          </div>

          {/* Bring an abandoned setup TO the customer instead of leaving it on
              a page they have to find. Renders nothing when there is nothing to
              resume. The same component belongs on the dashboard's next-action
              band (cross-branch: feat/dashboard-console). */}
          <ResumeSetupPrompt className="mb-6" />

          <div className="mb-12">
            {/* Controls Bar - Always visible */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              {/* Count/Ownership Filter Bar */}
              <div className="hidden md:flex items-center gap-3">
                {agentsLoading ? (
                  <div className="h-9 w-64 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded animate-pulse" />
                ) : teamCount > 0 ? (
                  <>
                    <button
                      onClick={() => setOwnershipFilter("all")}
                      className={`text-sm px-3 py-1.5 rounded transition-colors ${
                        ownershipFilter === "all"
                          ? "bg-gray-200 text-gray-900 font-semibold"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      }`}
                    >
                      Total - <span className="font-semibold">{totalAgents}</span>
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => setOwnershipFilter("personal")}
                      className={`text-sm px-3 py-1.5 rounded transition-colors ${
                        ownershipFilter === "personal"
                          ? "bg-gray-200 text-gray-900 font-semibold"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      }`}
                    >
                      Personal - <span className="font-semibold">{personalCount}</span>
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => setOwnershipFilter("team")}
                      className={`text-sm px-3 py-1.5 rounded transition-colors ${
                        ownershipFilter === "team"
                          ? "bg-gray-200 text-gray-900 font-semibold"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      }`}
                    >
                      Team - <span className="font-semibold">{teamCount}</span>
                    </button>
                  </>
                ) : (
                  <div className="text-sm px-3 py-1.5 rounded bg-gray-200 text-gray-900 font-semibold">
                    Total - <span className="font-semibold">{totalAgents}</span>
                  </div>
                )}
              </div>

              {/* Controls: Unfinished Setups, View Toggle and Search with gradient borders */}
              <div className="flex items-center gap-3 w-full md:w-auto justify-end">
                {unfinishedSetups.length > 0 && (
                  <button
                    onClick={() => navigate("/genie-hub/unfinished-setups")}
                    className="shrink-0 inline-flex items-center gap-2 min-h-[44px] min-w-[44px] sm:min-h-0 justify-center px-2.5 sm:px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                    aria-label={`${unfinishedSetups.length} unfinished genie setup${unfinishedSetups.length === 1 ? "" : "s"}`}
                  >
                    <Hourglass className="w-4 h-4" />
                    <span className="hidden sm:inline whitespace-nowrap">Unfinished setups</span>
                    <span
                      className="min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold text-white inline-flex items-center justify-center"
                      style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
                    >
                      {unfinishedSetups.length}
                    </span>
                  </button>
                )}

                {/* View Toggle with gradient border */}
                <div className="p-[2px] rounded-lg bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                  <div className="flex rounded-[10px] bg-white">
                    <button
                      onClick={() => handleViewModeChange("grid")}
                      className={`px-3 py-2 rounded-l-lg transition-colors flex items-center gap-2 ${
                        viewMode === "grid"
                          ? "bg-gray-100 text-gray-900 font-medium"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                      title="Grid View"
                    >
                      <Grid3X3 className="w-4 h-4" />
                      <span className="text-sm hidden sm:inline">Grid</span>
                    </button>
                    <button
                      onClick={() => handleViewModeChange("table")}
                      className={`px-3 py-2 rounded-r-lg transition-colors flex items-center gap-2 ${
                        viewMode === "table"
                          ? "bg-gray-100 text-gray-900 font-medium"
                          : "text-gray-600 hover:bg-gray-50"
                      }`}
                      title="Table View"
                    >
                      <TableProperties className="w-4 h-4" />
                      <span className="text-sm hidden sm:inline">Table</span>
                    </button>
                  </div>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="shrink-0 p-[2px] rounded-lg bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]"
                      aria-label={`Sort genies: ${activeSortLabel}`}
                      title="Sort genies"
                    >
                      <span
                        className={`flex items-center gap-2 px-3 py-2 min-h-[40px] rounded-[10px] bg-white transition-colors hover:bg-gray-50 ${
                          sortMode === "custom" ? "text-gray-600" : "text-gray-900 font-medium"
                        }`}
                      >
                        <ArrowUpDown className="w-4 h-4" />
                        <span className="text-sm hidden sm:inline whitespace-nowrap">{activeSortLabel}</span>
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 rounded-xl">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#64748B]">
                      Sort by
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuRadioGroup
                      value={sortMode}
                      onValueChange={(value) => handleSortModeChange(value as GenieSortMode)}
                    >
                      {SORT_OPTIONS.map((option) => (
                        <DropdownMenuRadioItem key={option.value} value={option.value} className="min-h-[44px] sm:min-h-0 cursor-pointer">
                          {option.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Search Input with gradient border */}
                <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] w-full md:w-auto">
                  <div className="rounded-[10px] bg-white">
                    <SearchInput
                      value={genieSearchTerm}
                      onChange={setGenieSearchTerm}
                      placeholder="Search genies..."
                      className="w-full md:w-80"
                      inputClassName="border-transparent focus-visible:ring-0"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Group Filter Tabs */}
            <div className="mb-6">
              {agentsLoading ? (
                <div className="h-10 w-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded-lg animate-pulse" />
              ) : (
                <GroupFilterTabs
                  groups={groups}
                  agents={agents}
                  selectedGroupId={selectedGroupId}
                  onGroupChange={setSelectedGroupId}
                  onCreateGroup={() => {
                    setEditingGroup(null);
                    setShowGroupDialog(true);
                  }}
                  totalCount={totalAgents}
                  serverGroupCounts={serverGroupCounts}
                />
              )}
            </div>

            {agentsLoading ? (
              <BrandSpinner label="Loading your genies..." className="min-h-0 py-16 bg-transparent" />
            ) : agents.length > 0 ? (
              <>
                {/* Show message when search filters out all genies */}
                {filteredAgents.length === 0 && genieSearchTerm ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">
                      No genies found matching "{genieSearchTerm}"
                    </p>
                    <button
                      onClick={() => setGenieSearchTerm("")}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <>
                    {viewMode === "table" ? (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={filteredAgents.map((agent) => agent.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <GenieTableView
                            agents={filteredAgents}
                            groups={groups}
                            isDragEnabled={isDragEnabled}
                            isReordering={isReordering}
                            showUnlockRow={canCreateGenie}
                          />
                        </SortableContext>
                      </DndContext>
                    ) : viewMode === "grid" ? (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={filteredAgents.map((agent) => agent.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredAgents.map((agent) => (
                              <MagicGenieCard
                                key={agent.id}
                                genie={agent}
                                groups={groups}
                                isDragEnabled={isDragEnabled}
                                isReordering={isReordering}
                                weeklyChats={activityCounts?.[agent.id]}
                              />
                            ))}
                            {canCreateGenie && <UnlockGenieCard />}
                            {!limitsLoading && !canCreateGenie && (
                              <UpgradePromptCard currentCount={blockingScope.currentGenieCount} maxGenies={blockingScope.maxGenies} />
                            )}
                          </div>
                        </SortableContext>
                      </DndContext>
                    ) : null}
                  </>
                )}

                {/* Infinite scroll trigger */}
                {hasNextPage && (
                  <div ref={loadMoreRef} className="flex justify-center py-8">
                    {isFetchingNextPage ? (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Loading more genies...</span>
                      </div>
                    ) : (
                      <Button
                        onClick={() => fetchNextPage()}
                        variant="outline"
                        className="px-6 py-2"
                      >
                        Load More Genies
                      </Button>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Empty State */
              <div className="text-center py-16">
                {isAtLimit && requiresUpgrade ? (
                  // User at limit - show upgrade prompt
                  <div className="max-w-2xl mx-auto">
                    <UpgradePromptCard currentCount={blockingScope.currentGenieCount} maxGenies={blockingScope.maxGenies} />
                  </div>
                ) : (
                  // User can create genies - show creation prompt
                  <>
                    <div className="w-24 h-24 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <Plus className="w-12 h-12 text-blue-600" />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                      No Genies Yet
                    </h2>
                    <p className="text-sm md:text-base text-gray-600 mb-6 md:mb-8 max-w-md mx-auto px-4">
                      Get started by creating your first AI assistant. Choose from various
                      categories and customize it to fit your specific needs.
                    </p>
                    <button
                      onClick={() => navigate("/user-hub/genie-setup")}
                      className="inline-flex items-center px-8 py-4 text-white rounded-xl font-semibold transition-all shadow-lg hover:opacity-90"
                      style={{
                        background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
                      }}
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Create Your First Genie
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Group Management Dialog */}
      <GroupManagementDialog
        isOpen={showGroupDialog}
        onClose={() => {
          setShowGroupDialog(false);
          setEditingGroup(null);
        }}
        group={editingGroup}
      />
    </div>
  );
};

export default MyGeniesPage;
